<script setup lang="ts">
import {
    Comment,
    Fragment,
    Text,
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    useAttrs,
    useId,
    useSlots,
    watch,
    type CSSProperties,
    type VNode
} from 'vue';
import { formSelectClasses } from '../form-control/classes';
import { resolveModalTeleportTarget, type ModalTeleportTarget } from '../ui-modal/teleportTarget';

defineOptions({
    name: 'IdeoFormSelect',
    inheritAttrs: false
});

type SelectOption = {
    value: any;
    text: string;
    disabled: boolean;
    hidden: boolean;
    group?: string;
    icon?: string;
    iconClass?: string;
    help?: string;
    dot?: string;
    dotDark?: string;
    meta?: string;
    placeholder?: boolean;
    empty?: boolean;
    depth?: number;
};

type SelectSize = 'sm' | 'md' | 'lg';
type PanelPlacement = 'bottom' | 'top';

const panelDistance = 5;
const viewportGap = 12;
const panelMaxHeight = 320;
const selectSizeClasses: Record<SelectSize, string> = {
    sm: '!px-[8px] !text-[0.78125rem]',
    md: '!px-[10px] !text-ui-body',
    lg: '!px-[14px] !text-[0.875rem]'
};

const model = defineModel<any>();
const props = withDefaults(defineProps<{
    valueField?: string;
    textField?: string;
    disabledField?: string;
    options?: any[];
    placeholder?: string;
    disabled?: boolean;
    readonly?: boolean;
    searchable?: boolean;
    allowEmpty?: boolean;
    emptyText?: string;
    icon?: string;
    iconSize?: 14 | 16;
    display?: 'default' | 'status';
    panelWidth?: 'trigger' | 'content';
    size?: SelectSize;
}>(), {
    options: () => [],
    valueField: 'value',
    textField: 'text',
    disabledField: 'disabled',
    placeholder: undefined,
    disabled: false,
    readonly: false,
    searchable: false,
    emptyText: undefined,
    icon: undefined,
    iconSize: 14,
    display: 'default',
    panelWidth: 'trigger',
    size: 'md'
});

const emit = defineEmits<{
    (e: 'change', value: any): void;
    (e: 'update:modelValue', value: any): void;
    (e: 'show'): void;
    (e: 'hide'): void;
}>();

const { $t } = useLocalization();
const attrs = useAttrs();
const slots = useSlots();
const root = ref<HTMLElement | null>(null);
const trigger = ref<HTMLButtonElement | null>(null);
const panel = ref<HTMLElement | null>(null);
const open = ref(false);
const positioned = ref(false);
const query = ref('');
const hotIndex = ref(0);
const placement = ref<PanelPlacement>('bottom');
const panelStyle = ref<CSSProperties>({});
const teleportTo = ref<ModalTeleportTarget>('body');
const instanceId = `ideo-form-select-${useId().replaceAll(':', '')}`;
const listboxId = `${instanceId}-listbox`;

let resizeObserver: ResizeObserver | null = null;
let positionFrame: number | null = null;
let contentAnimations: Animation[] = [];

function nodeName(node: VNode)
{
    const type = node.type as any;

    return type?.name ?? type?.['__name'];
}

function nodeText(nodes: VNode[] | VNode | string | null | undefined): string
{
    if (typeof nodes === 'string')
        return nodes.trim();

    if (!nodes)
        return '';

    const list = Array.isArray(nodes) ? nodes : [nodes];

    return list.map((node: any) =>
    {
        if (typeof node === 'string')
            return node;

        if (node.type === Text)
            return typeof node.children === 'string' ? node.children : '';

        if (node.type === Comment)
            return '';

        if (node.type === Fragment && Array.isArray(node.children))
            return nodeText(node.children as VNode[]);

        if (typeof node.children === 'string')
            return node.children;

        if (typeof node.children?.default === 'function')
            return nodeText(node.children.default());

        if (Array.isArray(node.children))
            return nodeText(node.children as VNode[]);

        return '';
    }).join('').trim();
}

function mapOption(option: any, group?: string): SelectOption
{
    if (option === null || typeof option !== 'object')
    {
        return {
            value: option,
            text: String(option ?? ''),
            disabled: false,
            hidden: false,
            group
        };
    }

    return {
        value: option[props.valueField],
        text: Number.isFinite(option.level) ? String(option[props.textField] ?? option.label ?? '').trimStart() : String(option[props.textField] ?? option.label ?? ''),
        depth: Number.isFinite(option.level) ? Math.max(0, Math.floor(option.level)) : 0,
        disabled: Boolean(option[props.disabledField]),
        hidden: Boolean(option.hidden),
        group: group ?? option.group,
        icon: option.icon,
        iconClass: option.iconClass,
        help: option.help,
        dot: option.dot,
        dotDark: option.dotDark,
        meta: option.meta
    };
}

function parseNodes(nodes: VNode[], group?: string): SelectOption[]
{
    return nodes.flatMap((node) =>
    {
        if (node.type === Comment || node.type === Text)
            return [];

        if (node.type === Fragment && Array.isArray(node.children))
            return parseNodes(node.children as VNode[], group);

        const name = nodeName(node);

        if (name === 'IdeoFormSelectOption')
        {
            return [{
                value: node.props?.value,
                text: nodeText(typeof node.children === 'object' ? (node.children as any)?.default?.() : node.children as any),
                disabled: Boolean(node.props?.disabled),
                hidden: Boolean(node.props?.hidden),
                group
            }];
        }

        if (name === 'IdeoFormSelectOptionGroup')
        {
            const label = String(node.props?.label ?? '');
            const children = typeof node.children === 'object' ? (node.children as any)?.default?.() : [];
            const declaredOptions = Array.isArray(node.props?.options)
                ? node.props.options.map((option: Record<string, any>) => ({
                    value: option[node.props?.valueField ?? 'value'],
                    text: String(option[node.props?.textField ?? 'text'] ?? ''),
                    disabled: Boolean(option.disabled),
                    hidden: Boolean(option.hidden),
                    group: label
                }))
                : [];

            return children?.length ? parseNodes(children, label) : declaredOptions;
        }

        if (Array.isArray(node.children))
            return parseNodes(node.children as VNode[], group);

        return [];
    });
}

const declaredOptions = computed<SelectOption[]>(() =>
{
    const firstOptions = parseNodes(slots.first?.() ?? []);
    const contentOptions = parseNodes(slots.default?.() ?? []);
    const options = [
        ...firstOptions,
        ...(contentOptions.length > 0 ? contentOptions : props.options.map(option => mapOption(option)))
    ];
    const allowEmpty = props.allowEmpty ?? (firstOptions.length === 0 && contentOptions.length === 0 && typeof props.placeholder === 'string');

    if (allowEmpty)
    {
        const emptyText = props.emptyText ?? '[[[Brak wyboru]]]';

        return [{
            value: null,
            text: $t(emptyText),
            disabled: false,
            hidden: false,
            placeholder: true,
            empty: true,
            icon: 'circle-off'
        }, ...options.filter(option => !Object.is(option.value, null))];
    }

    return options;
});

const selected = computed(() => declaredOptions.value.find(option => Object.is(option.value, model.value)));
const hasSelectedValue = computed(() => !!selected.value && !selected.value.placeholder);
const visibleOptions = computed(() =>
{
    const phrase = query.value.trim().toLocaleLowerCase();

    return declaredOptions.value.filter((option) =>
    {
        if (option.hidden)
            return false;

        if (option.empty)
            return !phrase;

        if (!phrase)
            return true;

        return `${option.text} ${option.help ?? ''} ${option.meta ?? ''}`.toLocaleLowerCase().includes(phrase);
    });
});

const rootClass = computed(() => attrs.class);
const panelPositionClass = computed(() => teleportTo.value === 'body' ? 'fixed' : 'absolute');
const fieldName = computed(() => typeof attrs.name === 'string' ? attrs.name : undefined);
const fieldForm = computed(() => typeof attrs.form === 'string' ? attrs.form : undefined);
// Opcje, które człowiek może wybrać z listy: widoczne i czynne. Tylko one trafiają do nośnika,
// więc wartość spoza nich agent dostaje z odmową, a nie w modelu
const carrierOptions = computed(() => declaredOptions.value.filter(option => !option.disabled && !option.hidden));
// Nośnik powtarza zdarzenia wyboru człowieka: model dostał już wartość od agenta, ale reguły widoku
// wiszą na "update:modelValue" i "change" kontrolki, a nie na modelu. Wartość z opcji, nie z modelu,
// bo to ona ma rodzaj, który wysyła wybór człowieka (liczba zamiast tekstu)
const carrierValue = computed({
    get: () => model.value,
    set: (value: any) =>
    {
        emit('update:modelValue', value);
        emit('change', value);
    }
});
const triggerAttrs = computed(() =>
{
    const result = { ...attrs } as Record<string, any>;

    delete result.class;
    delete result.name;
    delete result.form;

    return result;
});
const triggerClasses = computed(() => [
    formSelectClasses,
    selectSizeClasses[props.size],
    'group',
    open.value ? '!border-accent !ring-[3px] !ring-[color-mix(in_oklch,var(--color-accent)_20%,transparent)]' : '',
    hasSelectedValue.value ? 'text-fg dark:text-fg-dark' : 'text-fg-faint dark:text-fg-faint-dark',
    props.readonly ? 'cursor-default' : 'cursor-pointer'
]);
const activeOptionId = computed(() => open.value && visibleOptions.value[hotIndex.value]
    ? `${listboxId}-option-${hotIndex.value}`
    : undefined);

function isSelected(option: SelectOption)
{
    return Object.is(option.value, model.value);
}

function enabledIndex(position: 'first' | 'last')
{
    if (position === 'first')
        return visibleOptions.value.findIndex(option => !option.disabled);

    return visibleOptions.value.findLastIndex(option => !option.disabled);
}

function selectedIndex()
{
    const index = visibleOptions.value.findIndex(option => isSelected(option) && !option.disabled);

    return index >= 0 ? index : Math.max(0, enabledIndex('first'));
}

function schedulePosition()
{
    if (!open.value || positionFrame !== null)
        return;

    positionFrame = window.requestAnimationFrame(() =>
    {
        positionFrame = null;
        updatePosition();
    });
}

function updatePosition()
{
    if (!open.value || !trigger.value || !panel.value)
        return;

    const triggerRect = trigger.value.getBoundingClientRect();
    const distance = props.display === 'status' ? 4 : panelDistance;
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const modal = teleportTo.value instanceof HTMLElement
        ? teleportTo.value.closest('dialog')
        : null;
    const modalRect = modal?.getBoundingClientRect();
    const scaleX = modalRect && modal?.offsetWidth ? modalRect.width / modal.offsetWidth : 1;
    const scaleY = modalRect && modal?.offsetHeight ? modalRect.height / modal.offsetHeight : 1;
    const originLeft = modalRect && modal ? modalRect.left + (modal.clientLeft - modal.scrollLeft) * scaleX : 0;
    const originTop = modalRect && modal ? modalRect.top + (modal.clientTop - modal.scrollTop) * scaleY : 0;
    let contentWidth = triggerRect.width;

    if (props.panelWidth === 'content')
    {
        const element = panel.value;
        const previousWidth = element.style.width;
        const previousMinWidth = element.style.minWidth;
        const previousMaxWidth = element.style.maxWidth;

        element.style.width = 'max-content';
        element.style.minWidth = '0';
        element.style.maxWidth = 'none';
        contentWidth = element.getBoundingClientRect().width;
        element.style.width = previousWidth;
        element.style.minWidth = previousMinWidth;
        element.style.maxWidth = previousMaxWidth;
    }

    const width = Math.min(Math.max(triggerRect.width, contentWidth), viewportWidth - (viewportGap * 2));
    const left = Math.min(
        Math.max(viewportGap, triggerRect.left),
        Math.max(viewportGap, viewportWidth - viewportGap - width)
    );
    const availableBelow = viewportHeight - viewportGap - triggerRect.bottom - distance;
    const availableAbove = triggerRect.top - viewportGap - distance;
    const contentHeight = panel.value.scrollHeight * scaleY;
    const useTop = availableBelow < Math.min(contentHeight, panelMaxHeight * scaleY) && availableAbove > availableBelow;
    const availableHeight = Math.max(0, useTop ? availableAbove : availableBelow);
    const maxHeight = Math.min(panelMaxHeight * scaleY, availableHeight);
    const panelHeight = Math.min(contentHeight, maxHeight);

    placement.value = useTop ? 'top' : 'bottom';
    panelStyle.value = {
        left: `${Math.round((left - originLeft) / scaleX)}px`,
        top: `${Math.round(((useTop ? triggerRect.top - distance - panelHeight : triggerRect.bottom + distance) - originTop) / scaleY)}px`,
        width: `${Math.round(width / scaleX)}px`,
        minWidth: `${Math.round(width / scaleX)}px`,
        maxWidth: `${Math.round(width / scaleX)}px`,
        maxHeight: `${Math.floor(maxHeight / scaleY)}px`
    };
    positioned.value = true;
}

function onModalTransitionEnd(event: TransitionEvent)
{
    if (event.target === event.currentTarget && event.propertyName === 'scale')
        schedulePosition();
}

function startPositioning()
{
    window.addEventListener('resize', schedulePosition);
    window.addEventListener('scroll', schedulePosition, true);

    if (teleportTo.value instanceof HTMLElement)
        teleportTo.value.closest('dialog')?.addEventListener('transitionend', onModalTransitionEnd);

    if (typeof ResizeObserver !== 'undefined')
    {
        resizeObserver = new ResizeObserver(schedulePosition);

        if (trigger.value)
            resizeObserver.observe(trigger.value);

        if (panel.value)
            resizeObserver.observe(panel.value);

        const modal = teleportTo.value instanceof HTMLElement
            ? teleportTo.value.closest('dialog')
            : null;

        if (modal)
            resizeObserver.observe(modal);
    }
}

function stopPositioning()
{
    window.removeEventListener('resize', schedulePosition);
    window.removeEventListener('scroll', schedulePosition, true);

    if (teleportTo.value instanceof HTMLElement)
        teleportTo.value.closest('dialog')?.removeEventListener('transitionend', onModalTransitionEnd);

    resizeObserver?.disconnect();
    resizeObserver = null;

    if (positionFrame !== null)
    {
        window.cancelAnimationFrame(positionFrame);
        positionFrame = null;
    }
}

function clearContentAnimations()
{
    contentAnimations.forEach(animation => animation.cancel());
    contentAnimations = [];
}

function animateContent(element: Element, opening: boolean)
{
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    {
        clearContentAnimations();

        return;
    }

    const panelElement = element as HTMLElement;
    const children = [...panelElement.children] as HTMLElement[];
    const interrupted = contentAnimations.length > 0;
    const currentOpacity = new Map(children.map(child => [child, Number.parseFloat(getComputedStyle(child).opacity) || 0]));

    clearContentAnimations();

    contentAnimations = children.map((child) =>
    {
        const from = opening && !interrupted ? 0 : currentOpacity.get(child) ?? (opening ? 0 : 1);
        const animation = child.animate([
            { opacity: from },
            { opacity: opening ? 1 : 0 }
        ], {
            duration: 140,
            delay: opening ? 35 : 0,
            easing: opening ? 'cubic-bezier(.2, .8, .3, 1)' : 'cubic-bezier(.4, 0, 1, 1)',
            fill: 'both'
        });

        if (opening)
        {
            animation.onfinish = () =>
            {
                animation.cancel();
                contentAnimations = contentAnimations.filter(item => item !== animation);
            };
        }

        return animation;
    });
}

async function show(initialHotIndex: number = -1)
{
    if (props.disabled || props.readonly || open.value)
        return;

    teleportTo.value = resolveModalTeleportTarget(root.value);
    query.value = '';
    hotIndex.value = initialHotIndex;
    positioned.value = false;
    open.value = true;
    emit('show');
    document.dispatchEvent(new CustomEvent('ideo-dropdown:open', {
        detail: {
            instance: instanceId,
            trigger: trigger.value
        }
    }));

    await nextTick();
    updatePosition();
    startPositioning();
    await nextTick();
    centerSelectedOption();
}

async function hide(restoreFocus = false)
{
    if (!open.value)
        return;

    open.value = false;
    positioned.value = false;
    query.value = '';
    stopPositioning();
    emit('hide');

    if (restoreFocus)
    {
        await nextTick();
        trigger.value?.focus();
    }
}

function toggle()
{
    if (open.value)
        hide();
    else
        show();
}

function pick(option: SelectOption)
{
    if (option.disabled)
        return;

    model.value = option.value;
    emit('change', option.value);
    hide(true);
}

function moveHot(direction: 1 | -1)
{
    if (visibleOptions.value.length === 0)
        return;

    let next = hotIndex.value;

    if (next < 0)
        next = direction === 1 ? -1 : 0;

    for (let attempts = 0; attempts < visibleOptions.value.length; attempts++)
    {
        next = (next + direction + visibleOptions.value.length) % visibleOptions.value.length;

        if (!visibleOptions.value[next]?.disabled)
        {
            hotIndex.value = next;

            return;
        }
    }
}

function jumpHot(position: 'first' | 'last')
{
    const index = enabledIndex(position);

    if (index >= 0)
        hotIndex.value = index;
}

function onKeydown(event: KeyboardEvent)
{
    if (props.disabled || props.readonly)
        return;

    if (!open.value && ['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key))
    {
        event.preventDefault();
        show(event.key === 'ArrowUp' ? enabledIndex('last') : selectedIndex());

        return;
    }

    if (!open.value)
        return;

    if (event.key === 'Escape')
    {
        event.preventDefault();
        hide(true);
    }
    else if (event.key === 'Tab')
    {
        const direction = event.shiftKey ? -1 : 1;
        let next = hotIndex.value < 0
            ? (direction === 1 ? 0 : visibleOptions.value.length - 1)
            : hotIndex.value + direction;

        while (next >= 0 && next < visibleOptions.value.length && visibleOptions.value[next]?.disabled)
            next += direction;

        if (next >= 0 && next < visibleOptions.value.length)
        {
            event.preventDefault();
            event.stopPropagation();
            hotIndex.value = next;
        }
        else
        {
            void hide();
            trigger.value?.focus({ preventScroll: true });
        }
    }
    else if (event.key === 'ArrowDown' || event.key === 'ArrowUp')
    {
        event.preventDefault();
        moveHot(event.key === 'ArrowDown' ? 1 : -1);
    }
    else if (event.key === 'Home' || event.key === 'End')
    {
        event.preventDefault();
        jumpHot(event.key === 'Home' ? 'first' : 'last');
    }
    else if (event.key === 'Enter')
    {
        event.preventDefault();

        const option = visibleOptions.value[hotIndex.value];

        if (option)
            pick(option);
    }
}

function centerSelectedOption()
{
    const scroller = panel.value?.querySelector<HTMLElement>('[data-ideo-form-select-scroll]');
    const option = scroller?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!scroller || !option)
    {
        scrollHotIntoView();
        return;
    }

    const scrollRect = scroller.getBoundingClientRect();
    const optionRect = option.getBoundingClientRect();
    const scale = scroller.offsetHeight ? scrollRect.height / scroller.offsetHeight : 1;
    if (scale <= 0)
        return;

    const target = scroller.scrollTop + (optionRect.top - scrollRect.top + optionRect.height / 2) / scale - scroller.clientHeight / 2;
    scroller.scrollTop = Math.max(0, Math.min(scroller.scrollHeight - scroller.clientHeight, target));
}

function scrollHotIntoView()
{
    if (!open.value || hotIndex.value < 0)
        return;

    panel.value
        ?.querySelector<HTMLElement>(`#${CSS.escape(`${listboxId}-option-${hotIndex.value}`)}`)
        ?.scrollIntoView({ block: 'nearest' });
}

function onPointerDown(event: PointerEvent)
{
    const path = event.composedPath();

    if (root.value && path.includes(root.value))
        return;

    if (panel.value && path.includes(panel.value))
        return;

    hide();
}

function onGlobalOpen(event: Event)
{
    const detail = (event as CustomEvent).detail;

    if (!open.value || detail?.instance === instanceId)
        return;

    hide();
}

function clearKeyboardHot()
{
    if (hotIndex.value >= 0)
        hotIndex.value = -1;
}

watch(query, async () =>
{
    hotIndex.value = Math.max(0, enabledIndex('first'));
    await nextTick();
    updatePosition();
});
watch(hotIndex, async () =>
{
    await nextTick();
    scrollHotIntoView();
});
watch(() => [props.disabled, props.readonly], ([disabled, readonly]) =>
{
    if (disabled || readonly)
        hide();
});
watch(visibleOptions, async () =>
{
    if (!open.value)
        return;

    hotIndex.value = Math.min(hotIndex.value, Math.max(0, visibleOptions.value.length - 1));
    await nextTick();
    updatePosition();
});

onMounted(() =>
{
    teleportTo.value = resolveModalTeleportTarget(root.value);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('ideo-dropdown:open', onGlobalOpen);
});

onBeforeUnmount(() =>
{
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('ideo-dropdown:open', onGlobalOpen);
    stopPositioning();
    clearContentAnimations();
});

defineExpose({
    show,
    hide,
    toggle
});
</script>

<template>
    <div ref="root" :class="['relative inline-flex w-full min-w-0 max-w-full select-none', rootClass]">
        <!-- Nośnik wartości pod nazwą pola: ukryta lista wyboru z tymi opcjami, które człowiek ma
             w menu, żeby wartość spoza nich dostała odmowę. Bez opcji nośnika nie ma, bo człowiek też
             nie ma wtedy czego wybrać -->
        <select v-if="fieldName && carrierOptions.length > 0" v-model="carrierValue" :name="fieldName" :form="fieldForm" :disabled="disabled" hidden>
            <option v-for="(option, index) in carrierOptions" :key="index" :value="option.value">{{ option.text }}</option>
        </select>
        <button
            ref="trigger"
            v-bind="triggerAttrs"
            type="button"
            role="combobox"
            aria-haspopup="listbox"
            :aria-expanded="String(open)"
            :aria-controls="listboxId"
            :aria-activedescendant="activeOptionId"
            :aria-readonly="readonly ? 'true' : undefined"
            :disabled="disabled"
            :class="triggerClasses"
            @click="toggle"
            @keydown="onKeydown"
        >
            <span class="flex min-w-0 flex-1 items-center" :class="props.display === 'status' ? 'gap-[8px]' : 'gap-[7px]'">
                <IdeoIcon v-if="icon" :name="icon" class="shrink-0 text-fg-muted dark:text-fg-muted-dark" :size="props.iconSize" />
                <IdeoIcon v-if="selected?.icon" :name="selected.icon" class="shrink-0" :class="selected.iconClass" :size="props.iconSize" data-select-selected-icon />
                <span v-if="selected?.dot" class="size-[8px] shrink-0 rounded-full bg-[var(--ideo-option-dot)] dark:bg-[var(--ideo-option-dot-dark)]" :style="{ '--ideo-option-dot': selected.dot, '--ideo-option-dot-dark': selected.dotDark || selected.dot }" aria-hidden="true" data-select-dot />
                <span class="min-w-0 flex-1 truncate">{{ selected?.text ?? placeholder ?? $t('[[[Wybierz…]]]') }}</span>
                <IdeoBadge v-if="selected?.meta" class="shrink-0">{{ selected.meta }}</IdeoBadge>
            </span>
            <IdeoIcon name="chevron-down" class="shrink-0 text-fg-faint group-hover:text-fg-muted dark:text-fg-faint-dark dark:group-hover:text-fg-muted-dark" :class="props.display === 'status' ? ['[transition:scale_0.16s_ease] motion-reduce:transition-none', open ? '[scale:1_-1]' : '[scale:1_1]'] : ''" :size="props.display === 'status' ? 14 : 13" aria-hidden="true" data-select-caret />
        </button>

        <Teleport :to="teleportTo">
            <Transition
                enter-active-class="[transition:opacity_0.14s_cubic-bezier(.2,.8,.3,1),scale_0.105s_cubic-bezier(.2,.8,.3,1)] motion-reduce:transition-none"
                enter-from-class="scale-[0.96] opacity-0"
                enter-to-class="scale-100 opacity-100"
                leave-active-class="[transition:opacity_0.14s_cubic-bezier(.4,0,1,1)_0.035s,scale_0.098s_cubic-bezier(.4,0,1,1)] motion-reduce:transition-none"
                leave-from-class="scale-100 opacity-100"
                leave-to-class="scale-[0.96] opacity-0"
                @enter="animateContent($event, true)"
                @leave="animateContent($event, false)"
                @after-leave="clearContentAnimations"
            >
                <div
                    v-if="open"
                    ref="panel"
                    :class="[
                        'isolate z-[800] flex select-none min-h-0 flex-col overflow-hidden rounded-[8px] border border-border bg-surface p-0 text-fg shadow-lg dark:border-border-dark dark:bg-surface-dark dark:text-fg-dark dark:shadow-[0_16px_40px_-10px_rgba(0,0,0,0.7),0_4px_12px_rgba(0,0,0,0.45)]',
                        panelPositionClass,
                        placement === 'top' ? 'origin-bottom' : 'origin-top',
                        positioned ? 'visible' : 'invisible'
                    ]"
                    :style="panelStyle"
                    data-ideo-form-select-panel
                    :data-select-display="props.display"
                    data-dropdown-surface-enter-duration="140"
                    data-dropdown-surface-leave-duration="140"
                    data-dropdown-content-enter-duration="140"
                    data-dropdown-content-leave-duration="140"
                    data-dropdown-surface-enter-delay="0"
                    data-dropdown-surface-leave-delay="35"
                    data-dropdown-content-enter-delay="35"
                    data-dropdown-content-leave-delay="0"
                >
                    <div v-if="searchable" class="relative shrink-0 px-[7px] pb-[6px] pt-[7px]">
                        <IdeoIcon name="search" class="pointer-events-none absolute start-[15px] top-[16px] z-1 text-fg-faint dark:text-fg-faint-dark" :size="13" aria-hidden="true" />
                        <IdeoFormInput
                            v-model="query"
                            class="select-text ps-[28px]"
                            type="search"
                            :aria-controls="listboxId"
                            :aria-activedescendant="activeOptionId"
                            :aria-label="$t('[[[Szukaj w opcjach]]]')"
                            :placeholder="$t('[[[Szukaj…]]]')"
                            @keydown="onKeydown"
                        />
                    </div>

                    <div :id="listboxId" class="flex min-h-0 min-w-0 flex-1 flex-col gap-[1px] overflow-y-auto px-[5px] pb-[5px]" :class="[searchable ? 'pt-0' : 'pt-[5px]', props.display === 'status' ? '!gap-[2px] !p-[4px]' : '']" role="listbox" data-ideo-form-select-scroll @pointermove.passive="clearKeyboardHot">
                        <IdeoEmptyState v-if="visibleOptions.length === 0" variant="compact" icon="search" title="[[[Brak wyników]]]" />
                        <template v-for="(option, index) in visibleOptions" :key="`${String(option.value)}-${index}`">
                            <div
                                v-if="option.group && (index === 0 || visibleOptions[index - 1]?.group !== option.group)"
                                class="shrink-0 px-[8px] pb-[4px] pt-[8px] text-ui-meta font-[600] uppercase tracking-[0.05em] text-fg-faint dark:text-fg-faint-dark"
                            >
                                {{ option.group }}
                            </div>
                            <IdeoComboBoxOption
                                :id="`${listboxId}-option-${index}`"
                                :selected="isSelected(option)"
                                :disabled="option.disabled"
                                :active="index === hotIndex"
                                :display="props.display"
                                :empty="option.empty"
                                class="relative"
                                :class="props.panelWidth === 'content' ? '!w-max min-w-full' : ''"
                                :style="option.depth ? { paddingInlineStart: `${10 + option.depth * 14}px` } : undefined"
                                @select="pick(option)"
                            >
                                <span v-for="depth in option.depth || 0" :key="depth" class="pointer-events-none absolute inset-y-0 border-l border-border-soft dark:border-border-soft-dark" :style="{ insetInlineStart: `${10 + (depth - 1) * 14}px` }" aria-hidden="true" data-select-depth-guide />
                                <span v-if="option.icon" class="inline-flex shrink-0 self-center items-center justify-center" :class="props.iconSize === 16 ? 'size-[16px]' : 'size-[14px]'" aria-hidden="true" data-select-option-icon>
                                    <IdeoIcon :name="option.icon" :class="option.iconClass || (isSelected(option) ? 'text-accent-strong dark:text-accent-strong-dark' : 'text-fg-faint dark:text-fg-faint-dark')" :size="props.iconSize" />
                                </span>
                                <span v-if="option.dot" class="size-[8px] shrink-0 rounded-full bg-[var(--ideo-option-dot)] dark:bg-[var(--ideo-option-dot-dark)]" :style="{ '--ideo-option-dot': option.dot, '--ideo-option-dot-dark': option.dotDark || option.dot }" aria-hidden="true" data-select-dot />
                                <span class="flex min-w-0 flex-1 flex-col gap-[1px]" :class="props.panelWidth === 'content' ? 'whitespace-nowrap [overflow-wrap:normal]' : ''">
                                    <span>{{ option.text || ' ' }}</span>
                                    <span v-if="option.help" class="text-ui-meta text-fg-faint dark:text-fg-faint-dark">{{ option.help }}</span>
                                </span>
                                <IdeoBadge v-if="option.meta" class="shrink-0">{{ option.meta }}</IdeoBadge>
                            </IdeoComboBoxOption>
                            <div v-if="option.empty" class="my-[4px] h-px shrink-0 bg-border-soft dark:bg-border-soft-dark" aria-hidden="true" />
                        </template>
                    </div>
                </div>
            </Transition>
        </Teleport>
    </div>
</template>
